import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart } from 'lucide-react';
import apiClient from '@/shared/services/apiClient';

interface PostReactionsProps {
  postId: number;
}

function getFingerprint(): string {
  let fp = localStorage.getItem('visitor_fp');
  if (!fp) {
    fp = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('visitor_fp', fp);
  }
  return fp;
}

export default function PostReactions({ postId }: PostReactionsProps) {
  const queryClient = useQueryClient();
  const fingerprint = getFingerprint();

  const { data } = useQuery<{ count: number; reacted: boolean }>({
    queryKey: ['reactions', postId],
    queryFn: async () => {
      const res = await apiClient.get(`/blog/posts/${postId}/reactions`, {
        params: { fingerprint },
      });
      return res.data;
    },
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/blog/posts/${postId}/reactions`, { fingerprint });
      return res.data;
    },
    onSuccess: (newData: { count: number; reacted: boolean }) => {
      queryClient.setQueryData(['reactions', postId], newData);
    },
  });

  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="flex items-center gap-1.5 text-sm bg-transparent border border-border rounded px-3 py-1.5 cursor-pointer font-mono transition-all hover:border-accent"
      style={{ color: data?.reacted ? 'var(--color-accent)' : 'var(--color-muted)' }}
      aria-label={data?.reacted ? 'Remove reaction' : 'React with heart'}
    >
      <Heart size={16} fill={data?.reacted ? 'var(--color-accent)' : 'none'} />
      <span>{data?.count ?? 0}</span>
    </button>
  );
}
